import type { AnyObject, ChangeStateType, ColumnsType } from "@similarweb/pro-logic-components";

import type { IRule } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

export enum EnrichedTabs {
    Keywords,
    Trends,
}

type WithTotalCount<T> = T & { totalCount: number };

export interface ITopicsEnrichedContainerProps {
    row: ITransformedTopicsData;
    expandedRowTabId: EnrichedTabs;
    onClose: () => void;
    showKeywordGroupButton?: boolean;
}
export interface IClicksChange {
    change: number;
    changeState: ChangeStateType;
    changeValueFormatted: string;
}

export interface IClicksShare {
    clicksShare: number;
    totalClicks: number;
}

interface IPositionChange {
    Position: number;
    PositionChange: number;
    PreviousPosition: number;
}

export interface ISerpFeatures {
    SearchTerm: string;
    SerpFeatures: string[];
    SiteSerpFeatures: { [key: string]: string[] };
}

export interface ISitesData {
    site: string;
    position: number;
    positionChange: number;
    previousPosition: number;
}

export interface ITopicsData {
    TopicId: string;
    Topic: string;
    TotalClicks: number;
    PreviousClicks: number;
    ClicksShare: number;
    Volume: number;
    Keywords: number;
    ClicksChange: number;
    OrganicShare: number;
}

export interface IKeywordsData {
    /* eslint-disable @typescript-eslint/naming-convention */
    Keyword: string;
    TotalClicks: number;
    PreviousClicks: number;
    ClicksShare: number;
    Change: number;
    Volume: number;
    OrganicShare: number;
    ZeroClicks: number;
    Position: number;
    PositionChange: number;
    PreviousPosition: number;
    WebsiteSerpFeatures: string[];
    TopUrl: string;
    KeywordSerpFeatures: string[];
    ChangeState: string;
    /* eslint-enable @typescript-eslint/naming-convention */
}

export interface ITransformedEnrichedKeywordsData
    extends Pick<IKeywordsData, "Keyword" | "ZeroClicks" | "TopUrl" | "OrganicShare" | "Volume"> {
    key: number;
    Position: { Value: number }[];
    ClicksShare: IClicksShare;
    ClicksChange: IClicksChange;
    positionChange: IPositionChange;
    serpFeatures: ISerpFeatures;
}

export interface ITransformedTopicsData extends Omit<ITopicsData, "ClicksChange" | "ClicksShare"> {
    ClicksChange: IClicksChange;
    ClicksShare: IClicksShare;
    key: number;
}

export type ITransformedTopics = WithTotalCount<{ data: ITransformedTopicsData[] }>;

export interface IPagesData {
    url: string;
    totalClicks: number;
    previousClicks: number;
    clicksShare: number;
    change: number;
    organicShare: number;
    topKeyword: string;
    keywords: number;
    changeState: string;
    keywordsCount: number;
}

export interface ITransformedEnrichedPagesData {
    ClicksChange: IClicksChange;
    key: number;
    url: string;
    totalClicks: number;
    previousClicks: number;
    clicksShare: number;
    change: number;
    organicShare: number;
    topKeyword: string;
    keywords: number;
    changeState: string;
}
export interface IDataResponse<T = ITopicsData | IKeywordsData | IPagesData> {
    Data: T[];
    TotalCount: number;
}

// Type aliases for backward compatibility
export type ITopicsResponse = IDataResponse<ITopicsData>;
export type IKeywordsResponse = Pick<IDataResponse<IKeywordsData>, "Data">;
export type IPagesResponse = IDataResponse<IPagesData>;

export type SortConfig = { sort: keyof ITransformedTopicsData; asc: boolean };
export interface ITopicsTable {
    type?: EKeywordTabs;
    data: ITransformedTopics["data"];
    isLoading: boolean;
    isError?: boolean;
    handleSort: (sorter: SortConfig) => void;
    sortConfig: SortConfig;
    selectedPageTab: string;
    showKeywordGroupButton?: boolean;
    totalCount: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    excelUrl?: string;
    requestBodyForExcel?: IRule[] & { field: string }[];
}

export type TableRecord = ITransformedTopicsData | ITransformedEnrichedKeywordsData | ITransformedEnrichedPagesData;

export type ColumnGenerator = (
    currentPage: number,
    pageSize: number,
    selectedPageTab: string,
    sortConfig: { sort: string; asc: boolean },
) => ColumnsType<AnyObject>;
